import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { 
  GridOptions, 
  GridReadyEvent,
  ValueFormatterParams 
} from 'ag-grid-community';
import { Box } from '@mui/material';
import { AgGridRibbon } from './AgGridRibbon';
import type { GridRibbonControl } from './AgGridRibbon';
import { useGridTheme } from '@/config/useGridTheme';
import { moneyFormat } from '@/utils/formatters';
import './AgGrid.css';

// Generic type for row data
export type { ColDef } from 'ag-grid-community';

export interface AgGridProps<TData> {
  classSuffix?: string;
  getRowId: (params: { data: TData }) => string;
  gridDataKey: string;
  gridOptions?: GridOptions;
  gridRef: React.RefObject<AgGridReact<TData>>;
  gridRibbonControls?: GridRibbonControl[] | null;
  rowData: TData[];
  height?: string;
  onGridReady?: (event: GridReadyEvent) => void;
  onRefresh?: () => void;
  refreshing?: boolean;
  autoRefreshIntervalMs?: number;
}

// Default grid options with standardized configurations
const getDefaultGridOptions = (): GridOptions => ({
  animateRows: true,
  enableCellTextSelection: true,
  ensureDomOrder: true,
  suppressMenuHide: true,
  rowSelection: {
    mode: 'multiRow',
    checkboxes: true,
    enableClickSelection: true
  },
  
  // Column types for consistent formatting
  columnTypes: {
    currency: {
      valueFormatter: (p: ValueFormatterParams) => {
        if (!p.value) return "";
        return moneyFormat(p.value, "USD");
      },
      width: 150,
      cellClass: 'ag-right-aligned-cell',
    },
    rightAligned: {
      cellClass: 'ag-right-aligned-cell',
    },
    numericColumn: {
      cellClass: 'ag-right-aligned-cell',
      filter: 'agNumberColumnFilter',
    },
  },

  // Default column definition
  defaultColDef: {
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 100,
  },

  // Status bar configuration
  statusBar: {
    statusPanels: [
      {
        statusPanel: 'agTotalRowCountComponent',
        align: 'left',
      },
      {
        statusPanel: 'agSelectedRowCountComponent',
        align: 'left',
      },
    ],
  },

  // Sidebar configuration
  sideBar: {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
      },
      {
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        toolPanel: 'agFiltersToolPanel',
      },
    ],
    defaultToolPanel: '',
  },
});

export const AgGrid = <TData,>({
  classSuffix = '',
  getRowId,
  gridDataKey,
  gridOptions = {},
  gridRef,
  gridRibbonControls,
  rowData,
  height = '600px',
  onGridReady,
  onRefresh,
  refreshing,
  autoRefreshIntervalMs,
}: AgGridProps<TData>) => {
  const internalGridRef = useRef<AgGridReact<TData>>(null);
  const actualGridRef = gridRef || internalGridRef;
  const gridTheme = useGridTheme();

  // State persistence key
  const stateKey = `gridstate-${gridDataKey}`;

  // Save grid state to localStorage
  const saveGridState = useCallback(() => {
    const gridApi = actualGridRef.current?.api;
    if (!gridApi) return;

    try {
      const state = {
        columnState: gridApi.getColumnState(),
        filterModel: gridApi.getFilterModel(),
        selectedRows: gridApi.getSelectedRows().map((row: TData) => getRowId({ data: row })),
        focusedColumn: gridApi.getFocusedCell()?.column.getColId(),
        focusedRowId: (() => {
          const focusedCell = gridApi.getFocusedCell();
          if (!focusedCell || focusedCell.rowPinned !== null) return null;
          const rowNode = gridApi.getDisplayedRowAtIndex(focusedCell.rowIndex);
          if (!rowNode || !rowNode.data) return null;
          return getRowId({ data: rowNode.data });
        })(),
      };
      
      localStorage.setItem(stateKey, JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to save grid state:', error);
    }
  }, [actualGridRef, getRowId, stateKey]);

  // Load grid state from localStorage
  const loadGridState = useCallback(() => {
    const gridApi = actualGridRef.current?.api;
    if (!gridApi) return;

    try {
      const savedState = localStorage.getItem(stateKey);
      if (!savedState) return;

      const state = JSON.parse(savedState);
      
      if (state.columnState) {
        gridApi.applyColumnState({ state: state.columnState, applyOrder: true });
      }
      
      if (state.filterModel) {
        gridApi.setFilterModel(state.filterModel);
      }
      
      // Restore selected rows
      if (state.selectedRows && state.selectedRows.length > 0) {
        setTimeout(() => {
          gridApi.forEachNode((node) => {
            if (node.data && state.selectedRows.includes(getRowId({ data: node.data }))) {
              node.setSelected(true);
            }
          });
        }, 100);
      }
    } catch (error) {
      console.warn('Failed to load grid state:', error);
    }
  }, [actualGridRef, getRowId, stateKey]);

  // Debounced save function
  const debouncedSave = useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(saveGridState, 1000);
    };
  }, [saveGridState]);

  // Grid ready handler
  const handleGridReady = useCallback((event: GridReadyEvent) => {
    loadGridState();
    if (onGridReady) {
      onGridReady(event);
    }
  }, [loadGridState, onGridReady]);

  // Save state on various events
  useEffect(() => {
    const gridApi = actualGridRef.current?.api;
    if (!gridApi) return;

    const events = [
      'columnMoved',
      'columnResized',
      'columnVisible',
      'filterChanged',
      'selectionChanged',
      'cellFocused',
    ];

    events.forEach(event => {
      gridApi.addEventListener(event as any, debouncedSave);
    });

    return () => {
      events.forEach(event => {
        gridApi.removeEventListener(event as any, debouncedSave);
      });
    };
  }, [actualGridRef, debouncedSave]);

  // Merge default options with provided options
  const mergedGridOptions = useMemo(() => {
    const defaults = getDefaultGridOptions();
    const merged = {
      ...defaults,
      theme: gridTheme,
      ...gridOptions,
      getRowId,
      onGridReady: handleGridReady,
    };

    // Convert deprecated rowSelection format if present
    if (merged.rowSelection === 'single' || merged.rowSelection === 'multiple') {
      merged.rowSelection = {
        mode: merged.rowSelection === 'single' ? 'singleRow' : 'multiRow',
        checkboxes: true,
        enableClickSelection: true
      };
    }

    return merged;
  }, [gridOptions, getRowId, handleGridReady, gridTheme]);

  const gridClass = classSuffix || '';

  return (
    <Box sx={{ height, width: '100%', display: 'flex', flexDirection: 'column' }}>
      {(gridRibbonControls === null) ? null : (
        <AgGridRibbon
          gridRef={actualGridRef}
          gridDataKey={gridDataKey}
          controls={gridRibbonControls}
          onRefresh={onRefresh}
          refreshing={refreshing}
          autoRefreshIntervalMs={autoRefreshIntervalMs}
        />
      )}
      
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <div className={gridClass} style={{ height: '100%', width: '100%' }}>
          <AgGridReact<TData>
            ref={actualGridRef}
            rowData={rowData}
            {...mergedGridOptions}
          />
        </div>
      </Box>
    </Box>
  );
};

export default AgGrid;